import type { ReactNode } from 'react';

import {
  QUERY_SENTIMENT_V3,
  type QuerySentimentV3Query,
  type QuerySentimentV3QueryVariables,
} from '@revelio/data-access';

import { useIsTaxonomyResolved } from '../../filters/contexts/FilterProvider';
import { useAvailableDateRangeWithTrialLag } from '../../filters/hooks/useAvailableDateRangeWithTrialLag';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import type { PrimaryFilterId } from '../../filters/types/filters';
import { getSentimentV3Variables } from '../../filters/utils/get-sentiment-v3-variables';
import { getV3MonthRefs } from '../../filters/utils/get-v3-month-refs';
import { useChart } from '../Chart/ChartProvider';
import type { SentimentChartType } from '../PageConfigProvider/types/chart';
import type { SentimentMetricValues } from '../PageConfigProvider/types/metric';
import { usePageState } from '../PageState/PageStateProvider';
import type {
  BreakdownSingleDataSet,
  DataProviderChildren,
  DataProviderQueryStatus,
  SingleDataSet,
  TimeseriesDataSet,
} from './types';
import { convertSentimentV3Data } from './utils/convert-sentiment-v3-data';
import { useUrqlQueryWithStatus } from './utils/data-provider-results-utils';

export type SentimentData =
  | SingleDataSet
  | TimeseriesDataSet
  | BreakdownSingleDataSet;

export const SentimentV3DataProvider = ({
  primaryDimension,
  metric,
  chartType,
  isSnapshot = true,
  children,
}: {
  primaryDimension: PrimaryFilterId;
  metric: SentimentMetricValues;
  chartType: SentimentChartType;
  isSnapshot?: boolean;
  children: DataProviderChildren<SentimentData | undefined>;
}) => {
  const filters = useEffectiveFilters({ viewGranularity: 'month' });
  const { pageState } = usePageState();
  const { item } = useChart();
  const selectedEntity = pageState.primaryEntities?.[item?.id];

  const variables = getSentimentV3Variables({
    filters,
    primaryDimension,
    isSnapshot,
  });

  const sentimentFilters = variables?.req?.filters;
  const { availableDateRange } = useAvailableDateRangeWithTrialLag();
  const monthRefs = getV3MonthRefs({
    availableDateRange,
    filters: sentimentFilters ?? {},
  });

  if (!variables) return null;

  return (
    <QuerySentimentV3 variables={variables}>
      {({ data, status }) => {
        const chartData = convertSentimentV3Data({
          data: data?.sentiment_v3?.entities ?? [],
          chartType,
          metric,
          monthRefs,
          selectedEntityId: selectedEntity?.id,
        });

        return children({ data: chartData, status });
      }}
    </QuerySentimentV3>
  );
};

export const SentimentV3DataProviderRaw = ({
  primaryDimension,
  isSnapshot = false,
  children,
}: {
  primaryDimension: PrimaryFilterId;
  isSnapshot?: boolean;
  children: (props: {
    data: QuerySentimentV3Query | undefined;
    status: DataProviderQueryStatus;
    monthRefs: string[];
  }) => ReactNode;
}) => {
  const filters = useEffectiveFilters({ viewGranularity: 'month' });

  const variables = getSentimentV3Variables({
    filters,
    primaryDimension,
    isSnapshot,
  });

  const { availableDateRange } = useAvailableDateRangeWithTrialLag();
  const monthRefs = getV3MonthRefs({
    availableDateRange,
    filters: variables?.req?.filters ?? {},
  });

  if (!variables) return null;

  return (
    <QuerySentimentV3 variables={variables}>
      {({ data, status }) => children({ data, status, monthRefs })}
    </QuerySentimentV3>
  );
};

type QuerySentimentV3Props = {
  variables: QuerySentimentV3QueryVariables;
  children: DataProviderChildren<QuerySentimentV3Query>;
};
const QuerySentimentV3 = ({ variables, children }: QuerySentimentV3Props) => {
  const isTaxonomyResolved = useIsTaxonomyResolved();
  const [{ data, status }] = useUrqlQueryWithStatus<QuerySentimentV3Query>({
    query: QUERY_SENTIMENT_V3,
    variables,
    pause: !isTaxonomyResolved,
  });

  return children({ data, status });
};
